/**
 * ZEO „Schreiben verfassen“ im großen ZEO (27.10.2026, Port der Pilot-Karte auf die EINE Korrespondenz): der Server hat einen VORSCHLAG vorbereitet
 * (Klient, Empfänger, Vorgang, Bezug, Aufgabe, Brieftext) – hier ist noch nichts gespeichert. Drei klar getrennte Schritte:
 *   „In Korrespondenz öffnen“  -> Vorschlag im Korrespondenz-Editor (bearbeiten, dort speichern)
 *   „Als Entwurf speichern“    -> bestehender Endpunkt (idempotent je ZEO-Nachricht), danach Rücklesen und Öffnen-Einstieg
 *   „Versenden …“              -> speichert den Entwurf und öffnet die Korrespondenz mit der finalen Vorschau + ausdrücklicher Bestätigung;
 *                                 die Karte sendet NIE selbst.
 * Quellen/Belegarten/offene Punkte: dieselbe Prüfansicht wie in der Korrespondenz (nicht Teil des Briefes).
 */
import { useState } from 'react';
import { ExternalLink, FileText, Loader2, Save, Send } from 'lucide-react';
import { schreibstubeAPI } from '@/api/schreibstubeAPI';
import { VERSANDWEGE, ausZeoVorschlag, entwurfBody, schreibstubeUrl } from '@/pages/schreibstube/schreibstube';
import PruefAnsicht from '@/pages/schreibstube/PruefAnsicht';

export function oeffneSchreibstube(params) {
  return window.open(schreibstubeUrl(params), 'olympus-schreibstube');
}

export default function ZeoSchreibEntwurfKarte({ entwurf, messageId }) {
  const [gespeichert, setGespeichert] = useState(null);       // zurückgelesener Entwurf
  const [laeuft, setLaeuft] = useState(null);
  const [fehler, setFehler] = useState(null);
  const weg = VERSANDWEGE.find((v) => v.key === entwurf.versandweg);

  const speichern = async () => {
    if (gespeichert) return gespeichert.id;
    const b = await schreibstubeAPI.anlegen({ ...entwurfBody(ausZeoVorschlag(entwurf, messageId)), zeo_message_id: messageId });
    const rb = await schreibstubeAPI.entwurf(b.id);             // Rücklesen: nur ein tatsächlich gespeicherter Entwurf gilt als gespeichert
    if (!rb || rb.id !== b.id) throw new Error('Der Entwurf ließ sich nach dem Speichern nicht zurücklesen – bitte in der Korrespondenz prüfen.');
    setGespeichert(rb);
    return rb.id;
  };
  const aktion = async (art) => {
    if (laeuft || !messageId) return;
    setLaeuft(art); setFehler(null);
    try {
      const id = await speichern();
      if (art === 'versenden') oeffneSchreibstube({ brief: id, aktion: 'versenden' });
    } catch (e) {
      setFehler(e.message || 'Speichern fehlgeschlagen – es wurde nichts gespeichert.');
    } finally {
      setLaeuft(null);
    }
  };

  return (
    <div className="mt-3 max-w-xl rounded-xl border border-violet-200 bg-white p-3 text-sm" data-testid="zeo-schreib-entwurf">
      <div className="flex items-center gap-1.5 font-semibold text-slate-700 mb-1.5"><FileText size={15} /> Schreiben – Vorschlag
        <span className="ml-auto text-[11px] font-normal text-slate-500" data-testid="zeo-se-status">
          {gespeichert ? `Entwurf gespeichert (${gespeichert.status || 'entwurf'}) – nicht versendet` : 'noch nicht gespeichert, nicht versendet'}
        </span>
      </div>
      <div className="grid grid-cols-[6.5rem_1fr] gap-x-2 gap-y-0.5 text-slate-600">
        <span className="text-slate-400">Für</span><span>{entwurf.klient_name || '—'}</span>
        <span className="text-slate-400">An</span><span data-testid="zeo-se-an">{entwurf.empfaenger_name || '— bitte wählen'}</span>
        <span className="text-slate-400">Versandweg</span><span data-testid="zeo-se-weg">{weg ? weg.label : '— bitte wählen'}</span>
        <span className="text-slate-400">Bezüglich</span><span>{entwurf.bezug ? `${entwurf.bezug.label} ${entwurf.bezug.wert}` : '—'}</span>
        <span className="text-slate-400">Vorgang</span><span>{entwurf.pruefung?.vorgang?.bezeichnung || (entwurf.vorgang_id ? 'verknüpft' : '—')}</span>
        <span className="text-slate-400">Aufgabe</span><span>{entwurf.aufgabe_titel || 'keine'}</span>
        <span className="text-slate-400">Betreff</span><span>{entwurf.betreff || '—'}</span>
      </div>
      {(entwurf.hinweise || []).length > 0 && (
        <ul className="mt-2 text-xs text-amber-800 bg-amber-50 rounded p-2 list-disc pl-5" data-testid="zeo-se-offen">
          {entwurf.hinweise.slice(0, 8).map((h, i) => <li key={i}>{h}</li>)}
        </ul>
      )}
      {entwurf.pruefung && <div className="mt-2"><PruefAnsicht pruefung={entwurf.pruefung} /></div>}
      <div className="flex flex-wrap gap-2 mt-3">
        <button type="button" onClick={() => oeffneSchreibstube(gespeichert ? { brief: gespeichert.id } : { zeoVorschlag: messageId })} disabled={!messageId}
          className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 hover:bg-violet-50 disabled:opacity-60" data-testid="zeo-se-oeffnen">
          <ExternalLink size={14} /> {gespeichert ? 'Entwurf in Korrespondenz öffnen' : 'In Korrespondenz öffnen und bearbeiten'}
        </button>
        <button type="button" onClick={() => aktion('speichern')} disabled={!!laeuft || !!gespeichert || !messageId}
          className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 hover:bg-violet-50 disabled:opacity-60" data-testid="zeo-se-speichern">
          {laeuft === 'speichern' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {gespeichert ? 'Als Entwurf gespeichert' : 'Als Entwurf speichern'}
        </button>
        <button type="button" onClick={() => aktion('versenden')} disabled={!!laeuft || !messageId}
          className="inline-flex items-center gap-1 rounded-md bg-violet-600 text-white px-2.5 py-1.5 hover:bg-violet-700 disabled:opacity-60" data-testid="zeo-se-versenden"
          title="Speichert den Entwurf und öffnet die finale Vorschau mit Bestätigung in der Korrespondenz – es wird nicht sofort gesendet">
          {laeuft === 'versenden' ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Versenden …
        </button>
      </div>
      {fehler && <p className="mt-2 text-xs text-red-700" data-testid="zeo-se-fehler">⚠️ {fehler}</p>}
      <p className="mt-2 text-[11px] text-slate-500">Versendet wird nur in der Korrespondenz – nach Vorschau und ausdrücklicher Bestätigung.</p>
    </div>
  );
}
